import { boxCenter, boxIoU, type DetectionBox, type DetectionClass } from './Detector';
import { KalmanFilter2D } from './KalmanFilter2D';
import { featureDistance, mergeFeature, type ReIdFeature } from './ReIdFeatures';

export type FilterBackend = 'alphabeta' | 'kalman';

export interface TrackedObject {
  id: number;
  class: DetectionClass;
  box: DetectionBox;
  velocity: { vx: number; vy: number };
  lastSeenAt: number;
  lastUpdatedAt: number;
  firstSeenAt: number;
  framesTracked: number;
  isGhost: boolean;
  feature?: ReIdFeature;
  kalman?: KalmanFilter2D;
}

export interface TrackerOptions {
  iouThreshold?: number;
  maxAgeMs?: number;
  alpha?: number;
  beta?: number;
  reidLifetimeMs?: number;
  reidThreshold?: number;
  filterBackend?: FilterBackend;
}

export interface UpdateResult {
  tracks: TrackedObject[];
  removed: number[];
  revived: number[];
}

interface GoneTrack {
  id: number;
  class: DetectionClass;
  feature: ReIdFeature;
  lastBox: DetectionBox;
  velocity: { vx: number; vy: number };
  goneAt: number;
  firstSeenAt: number;
  framesTracked: number;
}

function greedyIoUAssignByBoxes(
  tracks: readonly Pick<TrackedObject, 'class'>[],
  trackBoxes: readonly DetectionBox[],
  detections: readonly DetectionBox[],
  iouThreshold: number,
): Map<number, number> {
  if (trackBoxes.length === 0 || detections.length === 0) {
    return new Map<number, number>();
  }

  const pairs: { t: number; d: number; iou: number }[] = [];
  for (let t = 0; t < trackBoxes.length; t += 1) {
    for (let d = 0; d < detections.length; d += 1) {
      if (tracks[t].class !== detections[d].class) continue;
      const iou = boxIoU(trackBoxes[t], detections[d]);
      if (iou >= iouThreshold) pairs.push({ t, d, iou });
    }
  }
  pairs.sort((a, b) => b.iou - a.iou);
  const usedTracks = new Set<number>();
  const usedDets = new Set<number>();
  const assign = new Map<number, number>();
  for (const p of pairs) {
    if (usedTracks.has(p.t) || usedDets.has(p.d)) continue;
    usedTracks.add(p.t);
    usedDets.add(p.d);
    assign.set(p.t, p.d);
  }
  return assign;
}

export function greedyIoUAssign(
  tracks: readonly TrackedObject[],
  detections: readonly DetectionBox[],
  iouThreshold: number,
): Map<number, number> {
  const trackBoxes = tracks.map((track) => track.box);
  return greedyIoUAssignByBoxes(tracks, trackBoxes, detections, iouThreshold);
}

function predictBox(track: TrackedObject, now: number): DetectionBox {
  const dtSec = Math.max(0, (now - track.lastUpdatedAt) / 1000);
  if (track.kalman) {
    const p = track.kalman.position();
    const v = track.kalman.velocity();
    const a = track.kalman.acceleration();
    const nx = p.x + v.x * dtSec + 0.5 * a.x * dtSec * dtSec;
    const ny = p.y + v.y * dtSec + 0.5 * a.y * dtSec * dtSec;
    return { ...track.box, x: nx - track.box.w / 2, y: ny - track.box.h / 2 };
  }
  const c = boxCenter(track.box);
  const nx = c.x + track.velocity.vx * dtSec;
  const ny = c.y + track.velocity.vy * dtSec;
  return { ...track.box, x: nx - track.box.w / 2, y: ny - track.box.h / 2 };
}

function fuseObservation(
  track: TrackedObject,
  det: DetectionBox,
  feature: ReIdFeature | undefined,
  now: number,
  alpha: number,
  beta: number,
): TrackedObject {
  const dtSec = Math.max(1 / 240, (now - track.lastUpdatedAt) / 1000);
  const obs = boxCenter(det);
  let nextFeature = track.feature;
  if (feature) {
    nextFeature = nextFeature ? mergeFeature(nextFeature, feature) : feature;
  }

  if (track.kalman) {
    track.kalman.predict(dtSec);
    track.kalman.update(obs.x, obs.y);
    const p = track.kalman.position();
    const v = track.kalman.velocity();
    return {
      ...track,
      box: {
        class: det.class,
        x: p.x - det.w / 2,
        y: p.y - det.h / 2,
        w: det.w,
        h: det.h,
        score: det.score,
      },
      velocity: { vx: v.x, vy: v.y },
      lastSeenAt: now,
      lastUpdatedAt: now,
      framesTracked: track.framesTracked + 1,
      isGhost: false,
      feature: nextFeature,
    };
  }

  const prev = boxCenter(track.box);
  const newCx = prev.x + alpha * (obs.x - prev.x);
  const newCy = prev.y + alpha * (obs.y - prev.y);
  const obsVx = (obs.x - prev.x) / dtSec;
  const obsVy = (obs.y - prev.y) / dtSec;
  const newVx = track.velocity.vx + beta * (obsVx - track.velocity.vx);
  const newVy = track.velocity.vy + beta * (obsVy - track.velocity.vy);
  return {
    ...track,
    box: {
      class: det.class,
      x: newCx - det.w / 2,
      y: newCy - det.h / 2,
      w: det.w,
      h: det.h,
      score: det.score,
    },
    velocity: { vx: newVx, vy: newVy },
    lastSeenAt: now,
    lastUpdatedAt: now,
    framesTracked: track.framesTracked + 1,
    isGhost: false,
    feature: nextFeature,
  };
}

function extrapolate(track: TrackedObject, now: number): TrackedObject {
  const dtSec = Math.max(0, (now - track.lastUpdatedAt) / 1000);
  if (track.kalman) {
    track.kalman.predict(dtSec);
    const p = track.kalman.position();
    const v = track.kalman.velocity();
    return {
      ...track,
      box: { ...track.box, x: p.x - track.box.w / 2, y: p.y - track.box.h / 2 },
      velocity: { vx: v.x, vy: v.y },
      lastUpdatedAt: now,
      isGhost: true,
    };
  }
  const predicted = predictBox(track, now);
  return { ...track, box: predicted, lastUpdatedAt: now, isGhost: true };
}

function predictBoxes(tracks: readonly TrackedObject[], now: number): DetectionBox[] {
  const boxes = new Array<DetectionBox>(tracks.length);
  for (let i = 0; i < tracks.length; i += 1) {
    boxes[i] = predictBox(tracks[i], now);
  }
  return boxes;
}

function pruneExpiredGoneTracks(gonePool: GoneTrack[], now: number, reidLifetimeMs: number): void {
  let write = 0;
  for (let read = 0; read < gonePool.length; read += 1) {
    const gone = gonePool[read];
    if (now - gone.goneAt > reidLifetimeMs) continue;
    gonePool[write] = gone;
    write += 1;
  }
  gonePool.length = write;
}

export class Tracker {
  private tracks: TrackedObject[] = [];
  private gonePool: GoneTrack[] = [];
  private nextId = 1;
  private iouThreshold: number;
  private maxAgeMs: number;
  private reidLifetimeMs: number;
  private reidThreshold: number;
  private filterBackend: FilterBackend;
  private readonly alpha: number;
  private readonly beta: number;

  constructor(opts: TrackerOptions = {}) {
    this.iouThreshold = opts.iouThreshold ?? 0.3;
    this.maxAgeMs = opts.maxAgeMs ?? 3000;
    this.reidLifetimeMs = opts.reidLifetimeMs ?? 15000;
    this.reidThreshold = opts.reidThreshold ?? 0.35;
    this.filterBackend = opts.filterBackend ?? 'alphabeta';
    this.alpha = opts.alpha ?? 0.6;
    this.beta = opts.beta ?? 0.3;
  }

  reset(): void {
    this.tracks = [];
    this.gonePool = [];
    this.nextId = 1;
  }

  getTracks(): readonly TrackedObject[] {
    return this.tracks;
  }

  getGoneCount(): number {
    return this.gonePool.length;
  }

  setMaxAgeMs(ms: number): void {
    this.maxAgeMs = Math.max(100, ms);
  }

  setReidLifetimeMs(ms: number): void {
    this.reidLifetimeMs = Math.max(0, ms);
  }

  setReidThreshold(value: number): void {
    this.reidThreshold = Math.max(0, value);
  }

  setIoUThreshold(value: number): void {
    this.iouThreshold = Math.max(0, Math.min(1, value));
  }

  setFilterBackend(backend: FilterBackend): void {
    if (this.filterBackend === backend) return;
    this.filterBackend = backend;
    for (const t of this.tracks) {
      if (backend === 'kalman' && !t.kalman) {
        const c = boxCenter(t.box);
        t.kalman = new KalmanFilter2D(c.x, c.y);
      } else if (backend === 'alphabeta' && t.kalman) {
        t.kalman = undefined;
      }
    }
  }

  getFilterBackend(): FilterBackend {
    return this.filterBackend;
  }

  update(
    detections: readonly DetectionBox[],
    now: number,
    features?: ReadonlyMap<number, ReIdFeature>,
  ): UpdateResult {
    pruneExpiredGoneTracks(this.gonePool, now, this.reidLifetimeMs);

    const predictedBoxes = predictBoxes(this.tracks, now);
    const assign = greedyIoUAssignByBoxes(
      this.tracks,
      predictedBoxes,
      detections,
      this.iouThreshold,
    );
    const matchedDets = new Array<boolean>(detections.length).fill(false);
    for (const detIdx of assign.values()) matchedDets[detIdx] = true;
    const nextTracks: TrackedObject[] = [];
    const removed: number[] = [];
    const revived: number[] = [];

    for (let i = 0; i < this.tracks.length; i += 1) {
      const original = this.tracks[i];
      const detIdx = assign.get(i);
      if (detIdx !== undefined) {
        nextTracks.push(
          fuseObservation(
            original,
            detections[detIdx],
            features?.get(detIdx),
            now,
            this.alpha,
            this.beta,
          ),
        );
      } else if (now - original.lastSeenAt <= this.maxAgeMs) {
        nextTracks.push(extrapolate(original, now));
      } else if (original.feature && this.reidLifetimeMs > 0) {
        this.gonePool.push({
          id: original.id,
          class: original.class,
          feature: original.feature,
          lastBox: original.box,
          velocity: original.velocity,
          goneAt: now,
          firstSeenAt: original.firstSeenAt,
          framesTracked: original.framesTracked,
        });
      } else {
        removed.push(original.id);
      }
    }

    for (let d = 0; d < detections.length; d += 1) {
      if (matchedDets[d]) continue;
      const det = detections[d];
      const feature = features?.get(d);
      const reidMatchIdx = feature ? this.findReidMatch(det, feature) : -1;
      if (reidMatchIdx >= 0) {
        const gone = this.gonePool[reidMatchIdx];
        this.gonePool.splice(reidMatchIdx, 1);
        revived.push(gone.id);
        const c = boxCenter(det);
        nextTracks.push({
          id: gone.id,
          class: gone.class,
          box: det,
          velocity: gone.velocity,
          lastSeenAt: now,
          lastUpdatedAt: now,
          firstSeenAt: gone.firstSeenAt,
          framesTracked: gone.framesTracked + 1,
          isGhost: false,
          feature: mergeFeature(gone.feature, feature!),
          kalman: this.filterBackend === 'kalman' ? new KalmanFilter2D(c.x, c.y) : undefined,
        });
        continue;
      }
      const c = boxCenter(det);
      nextTracks.push({
        id: this.nextId,
        class: det.class,
        box: det,
        velocity: { vx: 0, vy: 0 },
        lastSeenAt: now,
        lastUpdatedAt: now,
        firstSeenAt: now,
        framesTracked: 1,
        isGhost: false,
        feature,
        kalman: this.filterBackend === 'kalman' ? new KalmanFilter2D(c.x, c.y) : undefined,
      });
      this.nextId += 1;
    }

    this.tracks = nextTracks;
    return { tracks: nextTracks, removed, revived };
  }

  private findReidMatch(det: DetectionBox, feature: ReIdFeature): number {
    let bestIdx = -1;
    let bestDist = this.reidThreshold;
    for (let i = 0; i < this.gonePool.length; i += 1) {
      const g = this.gonePool[i];
      if (g.class !== det.class) continue;
      const d = featureDistance(g.feature, feature);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    return bestIdx;
  }
}
